import React, { useState } from 'react';
import {
  Box,
  Typography,
  useTheme,
  alpha,
  Fade,
  Grow,
} from '@mui/material';
import { ChartSkeleton } from '../../../components/enterprise';
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  KeyboardArrowDown as ArrowIcon,
  Person as PersonIcon,
  CheckCircle as CheckIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { formatValue } from './chartUtils';

/**
 * Modern Funnel Chart Widget with Glassmorphism Design
 * A visually stunning conversion funnel with smooth animations
 */
const FunnelChart = ({ data, config = {} }) => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const [hoveredIndex, setHoveredIndex] = useState(null);

  if (data?.loading) {
    return <ChartSkeleton height="100%" />;
  }

  if (data?.error) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100%',
          minHeight: 200,
          p: 2,
        }}
      >
        <Typography color="error" variant="body2" align="center">
          {data.error}
        </Typography>
      </Box>
    );
  }

  // Extract funnel data
  let funnelData = [];
  if (data?.type === 'funnel' && Array.isArray(data?.data)) {
    funnelData = data.data;
  } else if (data?.type === 'timeseries' && Array.isArray(data?.data)) {
    // SQL timeseries: each series = one funnel stage. Series objects carry
    // {metric:{...}, values:[[ts,v],...]} — no count/value field — so derive
    // each stage's value from the LATEST point (mirrors Stat/Pie semantics).
    funnelData = data.data.map((series) => {
      const values = Array.isArray(series.values) ? series.values : [];
      const last = values.length ? values[values.length - 1] : null;
      const labels = series.metric || {};
      const name = series.name
        || Object.entries(labels).filter(([k]) => k !== '__name__').map(([, v]) => v).join(' · ')
        || labels.__name__ || 'stage';
      return { label: name, count: last ? Number(last[1]) || 0 : 0 };
    });
    // A funnel narrows: order stages largest-first so conversion math holds.
    funnelData.sort((a, b) => b.count - a.count);
  } else if (Array.isArray(data?.data)) {
    funnelData = data.data;
  } else if (Array.isArray(data)) {
    funnelData = data;
  }

  if (!funnelData || funnelData.length === 0) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100%',
          minHeight: 200,
          p: 2,
          gap: 2,
        }}
      >
        <Box
          sx={{
            width: 64,
            height: 64,
            borderRadius: '50%',
            background: isDarkMode
              ? 'linear-gradient(135deg, rgba(99, 102, 241, 0.2) 0%, rgba(139, 92, 246, 0.2) 100%)'
              : 'linear-gradient(135deg, rgba(99, 102, 241, 0.1) 0%, rgba(139, 92, 246, 0.1) 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <PersonIcon sx={{ fontSize: 32, color: '#6366F1', opacity: 0.5 }} />
        </Box>
        <Typography color="text.secondary" variant="body2">
          No funnel data available
        </Typography>
      </Box>
    );
  }

  const {
    useThousandSeparator = true,
  } = config;

  // Modern gradient colors with vibrant tones
  const gradientColors = [
    { from: '#6366F1', to: '#8B5CF6', glow: 'rgba(99, 102, 241, 0.4)' },
    { from: '#8B5CF6', to: '#A855F7', glow: 'rgba(139, 92, 246, 0.4)' },
    { from: '#A855F7', to: '#D946EF', glow: 'rgba(168, 85, 247, 0.4)' },
    { from: '#D946EF', to: '#EC4899', glow: 'rgba(217, 70, 239, 0.4)' },
    { from: '#EC4899', to: '#F43F5E', glow: 'rgba(236, 72, 153, 0.4)' },
    { from: '#F43F5E', to: '#EF4444', glow: 'rgba(244, 63, 94, 0.4)' },
  ];

  // Calculate metrics
  const maxCount = funnelData[0]?.count || funnelData[0]?.value || 1;
  const enrichedData = funnelData.map((step, index) => {
    const count = step.count || step.value || 0;
    const percentage = (count / maxCount) * 100;
    let conversionRate = 100;
    let dropOffRate = 0;
    let dropOffCount = 0;

    if (index > 0) {
      const prevCount = funnelData[index - 1]?.count || funnelData[index - 1]?.value || 0;
      conversionRate = prevCount > 0 ? (count / prevCount) * 100 : 0;
      dropOffRate = 100 - conversionRate;
      dropOffCount = prevCount - count;
    }

    return {
      ...step,
      count,
      percentage,
      conversionRate,
      dropOffRate,
      dropOffCount,
      label: step.label || step.step || step.name || `Step ${index + 1}`,
      colors: gradientColors[index % gradientColors.length],
    };
  });

  const totalEntry = enrichedData[0]?.count || 0;
  const finalConversion = enrichedData[enrichedData.length - 1]?.count || 0;
  const overallRate = totalEntry > 0 ? (finalConversion / totalEntry) * 100 : 0;

  // Format large numbers nicely
  // Delegates to the shared formatter (chartUtils) instead of a private copy.
  // This file and RumFunnelWidget carried byte-identical duplicates that
  // ignored the widget's unit/decimalPlaces config entirely.
  const formatNumber = (num) => formatValue(num, { unit: 'count' });

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        p: 2,
        overflow: 'auto',
        background: isDarkMode
          ? 'radial-gradient(ellipse at top, rgba(99, 102, 241, 0.05) 0%, transparent 50%)'
          : 'radial-gradient(ellipse at top, rgba(99, 102, 241, 0.03) 0%, transparent 50%)',
      }}
    >
      {/* Summary Stats Bar */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          gap: 4,
          mb: 3,
          pb: 2,
          borderBottom: 1,
          borderColor: isDarkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
        }}
      >
        <StatBadge
          label="Visitors"
          value={formatNumber(totalEntry)}
          icon={<PersonIcon sx={{ fontSize: 16 }} />}
          color="#6366F1"
          isDarkMode={isDarkMode}
        />
        <StatBadge
          label="Converted"
          value={formatNumber(finalConversion)}
          icon={<CheckIcon sx={{ fontSize: 16 }} />}
          color="#10B981"
          isDarkMode={isDarkMode}
        />
        <StatBadge
          label="Rate"
          value={`${overallRate.toFixed(1)}%`}
          icon={overallRate >= 5 ? <TrendingUpIcon sx={{ fontSize: 16 }} /> : <TrendingDownIcon sx={{ fontSize: 16 }} />}
          color={overallRate >= 5 ? '#10B981' : '#EF4444'}
          isDarkMode={isDarkMode}
        />
      </Box>

      {/* Funnel Visualization */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 0,
          position: 'relative',
        }}
      >
        {enrichedData.map((step, index) => {
          const isHovered = hoveredIndex === index;
          const isLast = index === enrichedData.length - 1;
          const widthPercent = Math.max(30, step.percentage);

          return (
            <Grow in key={index} timeout={300 + index * 100}>
              <Box
                sx={{
                  width: '100%',
                  maxWidth: 600,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                }}
              >
                {/* Funnel Step */}
                <Box
                  // The step's detail card was hover-only, so its numbers were
                  // unreachable without a mouse. Focus mirrors hover.
                  tabIndex={0}
                  onFocus={() => setHoveredIndex(index)}
                  onBlur={() => setHoveredIndex(null)}
                  onMouseEnter={() => setHoveredIndex(index)}
                  onMouseLeave={() => setHoveredIndex(null)}
                  sx={{
                    width: `${widthPercent}%`,
                    minWidth: 180,
                    position: 'relative',
                    cursor: 'pointer',
                    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                    transform: isHovered ? 'scale(1.03)' : 'scale(1)',
                    zIndex: isHovered ? 10 : 1,
                  }}
                >
                  {/* Main Bar */}
                  <Box
                    sx={{
                      height: 52,
                      borderRadius: 2,
                      background: `linear-gradient(135deg, ${step.colors.from} 0%, ${step.colors.to} 100%)`,
                      boxShadow: isHovered
                        ? `0 8px 32px ${step.colors.glow}, 0 0 0 1px rgba(255,255,255,0.1) inset`
                        : `0 4px 16px ${alpha(step.colors.from, 0.25)}`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      px: 2.5,
                      position: 'relative',
                      overflow: 'hidden',
                      transition: 'all 0.3s ease',
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        height: '50%',
                        background: 'linear-gradient(180deg, rgba(255,255,255,0.15) 0%, transparent 100%)',
                        borderRadius: '8px 8px 0 0',
                      },
                      '&::after': isHovered ? {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: '-100%',
                        width: '100%',
                        height: '100%',
                        background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)',
                        animation: 'shimmer 1s ease',
                        '@keyframes shimmer': {
                          '0%': { left: '-100%' },
                          '100%': { left: '100%' },
                        },
                      } : {},
                    }}
                  >
                    {/* Step Label */}
                    <Typography
                      sx={{
                        color: '#fff',
                        fontWeight: 600,
                        fontSize: '0.85rem',
                        textShadow: '0 1px 2px rgba(0,0,0,0.2)',
                        zIndex: 1,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        maxWidth: '45%',
                      }}
                    >
                      {step.label}
                    </Typography>

                    {/* Count & Percentage */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, zIndex: 1 }}>
                      <Typography
                        sx={{
                          color: '#fff',
                          fontWeight: 700,
                          fontSize: '1rem',
                          textShadow: '0 1px 2px rgba(0,0,0,0.2)',
                        }}
                      >
                        {formatNumber(step.count)}
                      </Typography>
                      <Box
                        sx={{
                          bgcolor: 'rgba(255,255,255,0.2)',
                          backdropFilter: 'blur(4px)',
                          px: 1,
                          py: 0.25,
                          borderRadius: 1,
                        }}
                      >
                        <Typography
                          sx={{
                            color: '#fff',
                            fontSize: '0.7rem',
                            fontWeight: 600,
                          }}
                        >
                          {step.percentage.toFixed(0)}%
                        </Typography>
                      </Box>
                    </Box>
                  </Box>

                  {/* Hover Details Card */}
                  <Fade in={isHovered}>
                    <Box
                      sx={{
                        position: 'absolute',
                        right: -200,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        width: 180,
                        p: 1.5,
                        borderRadius: 2,
                        background: isDarkMode
                          ? 'rgba(30, 30, 46, 0.95)'
                          : 'rgba(255, 255, 255, 0.98)',
                        backdropFilter: 'blur(12px)',
                        boxShadow: isDarkMode
                          ? '0 8px 32px rgba(0,0,0,0.4), 0 0 0 1px rgba(255,255,255,0.05)'
                          : '0 8px 32px rgba(0,0,0,0.12), 0 0 0 1px rgba(0,0,0,0.05)',
                        zIndex: 100,
                        display: isHovered ? 'block' : 'none',
                      }}
                    >
                      <Typography sx={{ fontWeight: 600, fontSize: '0.8rem', mb: 1, color: 'text.primary' }}>
                        {step.label}
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        <DetailRow label="Count" value={formatValue(step.count, { useThousandSeparator })} />
                        <DetailRow label="Of Total" value={`${step.percentage.toFixed(1)}%`} />
                        {index > 0 && (
                          <>
                            <DetailRow
                              label="Conversion"
                              value={`${step.conversionRate.toFixed(1)}%`}
                              color={step.conversionRate >= 70 ? '#10B981' : step.conversionRate >= 50 ? '#F59E0B' : '#EF4444'}
                            />
                            <DetailRow
                              label="Drop-off"
                              value={`${formatNumber(step.dropOffCount)}`}
                              color="#EF4444"
                            />
                          </>
                        )}
                      </Box>
                    </Box>
                  </Fade>
                </Box>

                {/* Connector */}
                {!isLast && (
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      py: 0.5,
                      position: 'relative',
                    }}
                  >
                    {/* Conversion Rate Badge */}
                    {enrichedData[index + 1] && (
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.5,
                          mb: 0.5,
                        }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            px: 1.5,
                            py: 0.25,
                            borderRadius: 10,
                            background: isDarkMode
                              ? alpha(enrichedData[index + 1].conversionRate >= 70 ? '#10B981' : enrichedData[index + 1].conversionRate >= 50 ? '#F59E0B' : '#EF4444', 0.15)
                              : alpha(enrichedData[index + 1].conversionRate >= 70 ? '#10B981' : enrichedData[index + 1].conversionRate >= 50 ? '#F59E0B' : '#EF4444', 0.1),
                            border: 1,
                            borderColor: alpha(enrichedData[index + 1].conversionRate >= 70 ? '#10B981' : enrichedData[index + 1].conversionRate >= 50 ? '#F59E0B' : '#EF4444', 0.3),
                          }}
                        >
                          {enrichedData[index + 1].conversionRate >= 70 ? (
                            <TrendingUpIcon sx={{ fontSize: 12, color: '#10B981' }} />
                          ) : enrichedData[index + 1].conversionRate < 50 ? (
                            <TrendingDownIcon sx={{ fontSize: 12, color: '#EF4444' }} />
                          ) : (
                            <WarningIcon sx={{ fontSize: 12, color: '#F59E0B' }} />
                          )}
                          <Typography
                            sx={{
                              fontSize: '0.7rem',
                              fontWeight: 600,
                              color: enrichedData[index + 1].conversionRate >= 70 ? '#10B981' : enrichedData[index + 1].conversionRate >= 50 ? '#F59E0B' : '#EF4444',
                            }}
                          >
                            {enrichedData[index + 1].conversionRate.toFixed(0)}%
                          </Typography>
                        </Box>
                      </Box>
                    )}

                    {/* Arrow */}
                    <ArrowIcon
                      sx={{
                        fontSize: 18,
                        color: isDarkMode ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.15)',
                      }}
                    />
                  </Box>
                )}
              </Box>
            </Grow>
          );
        })}
      </Box>

      {/* Bottom Insight */}
      <Box
        sx={{
          mt: 3,
          pt: 2,
          borderTop: 1,
          borderColor: isDarkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
          display: 'flex',
          justifyContent: 'center',
        }}
      >
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 1,
            px: 2,
            py: 1,
            borderRadius: 2,
            background: isDarkMode
              ? alpha(overallRate >= 5 ? '#10B981' : '#EF4444', 0.1)
              : alpha(overallRate >= 5 ? '#10B981' : '#EF4444', 0.08),
            border: 1,
            borderColor: alpha(overallRate >= 5 ? '#10B981' : '#EF4444', 0.2),
          }}
        >
          {overallRate >= 5 ? (
            <CheckIcon sx={{ fontSize: 18, color: '#10B981' }} />
          ) : (
            <WarningIcon sx={{ fontSize: 18, color: '#EF4444' }} />
          )}
          <Typography
            sx={{
              fontSize: '0.8rem',
              fontWeight: 500,
              color: 'text.secondary',
            }}
          >
            {overallRate >= 5 ? 'Healthy conversion rate' : 'Consider optimizing drop-off points'}
          </Typography>
          <Typography
            sx={{
              fontSize: '0.85rem',
              fontWeight: 700,
              color: overallRate >= 5 ? '#10B981' : '#EF4444',
            }}
          >
            {overallRate.toFixed(1)}%
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

// Stat Badge Component
const StatBadge = ({ label, value, icon, color, isDarkMode }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 1,
    }}
  >
    <Box
      sx={{
        width: 32,
        height: 32,
        borderRadius: '50%',
        background: alpha(color, isDarkMode ? 0.2 : 0.1),
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: color,
      }}
    >
      {icon}
    </Box>
    <Box>
      <Typography
        sx={{
          fontSize: '0.65rem',
          fontWeight: 500,
          color: 'text.secondary',
          textTransform: 'uppercase',
          letterSpacing: '0.5px',
          lineHeight: 1,
        }}
      >
        {label}
      </Typography>
      <Typography
        sx={{
          fontSize: '1rem',
          fontWeight: 700,
          color: 'text.primary',
          lineHeight: 1.2,
        }}
      >
        {value}
      </Typography>
    </Box>
  </Box>
);

// Detail Row Component
const DetailRow = ({ label, value, color }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
    <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>{label}</Typography>
    <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: color || 'text.primary' }}>{value}</Typography>
  </Box>
);

export default FunnelChart;
